import React from 'react';
import styles from './css/index.module.css';
import { Input } from '@fluentui/react-components';
import { CheckmarkCircleFilled } from '@fluentui/react-icons';
import { PatchProjectData, selectProject } from '../../redux/projectDataSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { useNavigate } from 'react-router';

const verifiedFromEnrichment = (fieldMeta, key) =>
    fieldMeta?.[key]?.source === 'custom' && fieldMeta?.[key]?.confidence === 'high';

const FieldLabel = ({ children, fieldMeta, metaKey }) => (
    <p className={`${styles.fieldLabel} d-flex align-items-center gap-1`}>
        {verifiedFromEnrichment(fieldMeta, metaKey) && (
            <CheckmarkCircleFilled style={{ color: '#107c10', flexShrink: 0 }} title="Verified from company enrichment" />
        )}
        {children}
    </p>
);

const AddBrandDetails = ({ setSelectedView, createdProjectData }) => {
    const dispatch = useDispatch();
    const { projectDetailButtonLoading } = useSelector(state => state.projectData);
    const fileInputRef = React.useRef();
    const navigate = useNavigate();
    const [isDragging, setIsDragging] = React.useState(false);
    const [formData, setFormData] = React.useState({
        linkedin: '',
        logoToShow: '',
    });
    const [uploadedFile, setUploadedFile] = React.useState();
    const [logoCleared, setLogoCleared] = React.useState(false);

    const handleChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async ({ isFinished }) => {
        let data = new FormData();
        if (uploadedFile) {
            data.append('logo', uploadedFile);
        } else if (!logoCleared && createdProjectData?.logo) {
            const logoVal = String(createdProjectData.logo).trim();
            // Browser fetch() to third-party image URLs is usually blocked by CORS; server accepts logo_url instead.
            if (/^https?:\/\//i.test(logoVal)) {
                data.append('logo_url', logoVal);
            } else {
                const file = await handleUrlInputChange(logoVal);
                if (file) data.append('logo', file);
            }
        }
        data.append('linkedin', formData?.linkedin);
        let response = await dispatch(PatchProjectData({ data: data, id: createdProjectData?.id }));
        if (isFinished) {
            if (response?.type === 'projectData/patchProjectData/fulfilled') {
                dispatch(selectProject(response.payload?.data));
                navigate('/dataroom');
            }
        } else setSelectedView('businessDetails');
    };

    const handleDivClick = () => {
        fileInputRef.current.click();
    };

    const handleClearLogo = e => {
        e.stopPropagation();
        setLogoCleared(true);
        setUploadedFile(undefined);
        setFormData(prev => ({ ...prev, logoToShow: '' }));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const processFile = file => {
        const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/svg+xml'];
        const isSvgByName = file?.name && file.name.toLowerCase().endsWith('.svg');
        if (file && (validImageTypes.includes(file.type) || (isSvgByName && (!file.type || file.type === 'image/svg+xml')))) {
            setLogoCleared(false);
            setUploadedFile(file);
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result;
                setFormData({ ...formData, logoToShow: base64 });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleFileChange = e => {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles?.length > 0) {
            processFile(selectedFiles[0]);
        }
    };

    const handleDragEnter = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        if (files?.length > 0) {
            processFile(files[0]);
        }
    };

    const handleUrlInputChange = async imageUrl => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const contentType = response.headers.get('content-type');
            const extension = contentType.split('/')[1];
            const fileName = `image.${extension}`;
            const fetchedFile = new File([blob], fileName, { type: contentType });
            return fetchedFile;
        } catch (error) {
            console.error('Error fetching image:', error);
        }
    };

    React.useEffect(() => {
        if (createdProjectData) {
            setLogoCleared(false);
            setFormData({
                logoToShow: createdProjectData?.logo,
                linkedin: createdProjectData?.linkedin,
            });
        }
    }, [createdProjectData]);

    const fieldMeta = createdProjectData?._fieldMeta;

    return (
        <div className={styles.componentCard}>
            <h5 className={styles.componentTitle}>Confirm brand details</h5>
            <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between">
                    <FieldLabel fieldMeta={fieldMeta} metaKey="logo">
                        Logo
                    </FieldLabel>
                    <div className="d-flex align-items-center gap-3">
                        <span className={styles.linkBtn} onClick={handleDivClick}>
                            Change
                        </span>
                        {formData?.logoToShow && (
                            <span className={styles.linkBtn} onClick={handleClearLogo}>
                                Clear
                            </span>
                        )}
                    </div>
                </div>
                <div
                    onClick={handleDivClick}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{ background: formData?.logoToShow && 'white' }}
                    className={`${styles.logoDiv} ${styles.uploadBox} ${isDragging ? styles.dragging : ''}`}>
                    {formData?.logoToShow ? (
                        <img className={styles.logoImage} alt={'logo'} src={formData?.logoToShow} />
                    ) : (
                        <span className={styles.uploadTextDragDrop}>
                            Drag & drop file or
                            <br />
                            click to upload
                            <br />
                            <br />
                            <small>(JPEG, PNG, JPG)</small>
                        </span>
                    )}

                    <input multiple={false} type="file" accept="image/jpeg,image/png,image/jpg,image/webp,image/svg+xml,.svg" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
                </div>
            </div>
            <div className="mb-4">
                <FieldLabel fieldMeta={fieldMeta} metaKey="linkedin">
                    LinkedIn profile
                </FieldLabel>
                <Input className={styles.inputField} value={formData?.linkedin} onChange={handleChange} name="linkedin" placeholder="www.linkedin.com/en-in/company-name" />
            </div>
            <div className="d-flex align-items-center justify-content-between">
                <button disabled={projectDetailButtonLoading} onClick={() => handleSubmit({ isFinished: true })} className={styles.submitButton}>
                    Confirm
                </button>
                <span onClick={() => handleSubmit({ isFinished: false })} className={styles.linkBtn}>
                    Add more details
                </span>
            </div>
        </div>
    );
};

export default AddBrandDetails;
